import React, { useState, useEffect } from 'react';
import {
  Card,
  List,
  Button,
  Space,
  Typography,
  Spin,
  message,
  Row,
  Col,
  Tag,
  Empty,
  Modal,
  InputNumber
} from 'antd';
import {
  AppstoreOutlined,
  UserOutlined,
  DownloadOutlined,
  SaveOutlined,
  EditOutlined,
  FilePdfOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import { TournamentService } from '@/services/tournament.service';
import { TournamentCategory, Participant } from '@/types/api.types';
import { logger } from '@/utils/logger';
import { sortTournamentCategories } from '@/utils/tournamentCategorySort';
import { useAuth } from '@/contexts/AuthContext';
import { ClassicBracketView } from './ClassicBracketView';

const { Title, Text } = Typography;

interface TournamentParticipant {
  id: number;
  participantId: number;
  tournamentId: number;
  categoryId: number;
  status: 'registered' | 'confirmed' | 'rejected';
  participant: {
    id: number;
    firstName: string;
    lastName: string;
    middleName?: string;
    weight?: number;
    beltLevel?: string;
    clubName?: string;
  };
  rank?: number; // Ранг участника (1-5 звезд)
  active?: boolean; // Активен ли участник в жеребьевке
}

interface CategoryWithParticipants extends TournamentCategory {
  participants: TournamentParticipant[];
  hasDraw?: boolean; // Есть ли уже сгенерированная жеребьевка
  matches?: any[]; // Матчи для этой категории
}

interface DrawsPageProps {
  tournamentId: number;
  onPublishBrackets?: () => Promise<void>;
  onBracketsGenerated?: () => void | Promise<void>;
  bracketsPublished?: boolean;
  hasMatches?: boolean;
  publishingBrackets?: boolean;
  tournamentDate?: string;
}

export const DrawsPage: React.FC<DrawsPageProps> = ({
  tournamentId,
  onPublishBrackets,
  onBracketsGenerated,
  bracketsPublished = false,
  hasMatches = false,
  publishingBrackets = false,
  tournamentDate
}) => {
  const { user } = useAuth();
  const [categories, setCategories] = useState<CategoryWithParticipants[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingParticipants, setLoadingParticipants] = useState<Set<number>>(new Set());
  const [selectedCategories, setSelectedCategories] = useState<Set<number>>(new Set());
  const [generating, setGenerating] = useState(false);
  const [bracketKey, setBracketKey] = useState(0); // Ключ для принудительного обновления сетки
  const [isGenerateModalVisible, setIsGenerateModalVisible] = useState(false);
  const [numTatami, setNumTatami] = useState<number>(1);
  const [tournament, setTournament] = useState<any>(null);
  const [isEditBracketModalVisible, setIsEditBracketModalVisible] = useState(false);
  const [regeneratingPdf, setRegeneratingPdf] = useState(false);
  const [reforming, setReforming] = useState(false);

  // Функция для автоматического подбора параметров на основе количества участников
  const calculateOptimalParams = (participantCount: number): { numTatami: number; generateType: 'olympic' | 'round_robin' } => {
    // Для круговой системы обычно используется при малом количестве участников (2-4)
    // Для олимпийской системы - при большем количестве
    const generateType: 'olympic' | 'round_robin' = participantCount <= 4 ? 'round_robin' : 'olympic';

    // Количество татами рассчитываем на основе количества участников
    // Примерно 8-16 участников на татами
    const numTatami = Math.max(1, Math.ceil(participantCount / 12));

    return { numTatami, generateType };
  };

  useEffect(() => {
    fetchCategories();
    // Загружаем данные турнира для проверки прав доступа
    const fetchTournament = async () => {
      try {
        const tournamentData = await TournamentService.getById(tournamentId);
        setTournament(tournamentData);
      } catch (error) {
        logger.error('Error fetching tournament:', error);
      }
    };
    fetchTournament();
  }, [tournamentId]);

  const fetchCategories = async () => {
    try {
      setLoading(true);

      // Загружаем категории и матчи параллельно для оптимизации
      const [tournamentCategories, matchesResponse] = await Promise.all([
        TournamentService.getTournamentCategories(tournamentId),
        TournamentService.getTournamentMatches(tournamentId).catch(() => null) // Игнорируем ошибки, если матчей нет
      ]);

      if (tournamentCategories.length === 0) {
        logger.warn('No categories found for tournament');
        message.warning('В турнире нет категорий');
        setCategories([]);
        return;
      }

      // Обрабатываем матчи один раз для всех категорий
      const matchesArray = matchesResponse
        ? (Array.isArray(matchesResponse) ? matchesResponse : (matchesResponse as any)?.data || [])
        : [];

      // Матчи в API: match.categoryId = tournament_categories.id (tc.id). Ключ Map = tc.id.
      const matchesByCategoryId = new Map<number, any[]>();
      matchesArray.forEach((m: any) => {
        const catId = m.categoryId;
        if (catId) {
          if (!matchesByCategoryId.has(catId)) {
            matchesByCategoryId.set(catId, []);
          }
          matchesByCategoryId.get(catId)!.push(m);
        }
      });

      // Загружаем участников для каждой категории параллельно
      // id категории: tournament_categories.id (tc.id); бэкенд принимает и tc.id, и categories.id
      const categoriesWithParticipants = await Promise.all(
        tournamentCategories.map(async (category) => {
          const tcId = Number(category.id);
          try {
            setLoadingParticipants(prev => new Set(prev).add(tcId));

            const participants = await TournamentService.getCategoryParticipants(
              tournamentId,
              tcId
            );

            // Матчи привязаны по tournament_categories.id — берём по category.id (tc.id)
            const categoryMatches = matchesByCategoryId.get(category.id) || [];
            const hasDraw = categoryMatches.length > 0;

            // Количество участников в категории — по матчам (как на вкладке «Распределение по татами»)
            const participantIdsInMatches = new Set<number>();
            categoryMatches.forEach((m: any) => {
              if (m.participant1Id) participantIdsInMatches.add(Number(m.participant1Id));
              if (m.participant2Id) participantIdsInMatches.add(Number(m.participant2Id));
            });
            const participantCountFromMatches = participantIdsInMatches.size;

            const mappedParticipants = Array.isArray(participants)
              ? participants.map((p: any) => ({
                ...p,
                rank: p.rank || 0,
                active: p.active !== false, // По умолчанию все активны
              }))
              : [];

            setLoadingParticipants(prev => {
              const newSet = new Set(prev);
              newSet.delete(tcId);
              return newSet;
            });

            return {
              ...category,
              participants: mappedParticipants,
              hasDraw,
              matches: categoryMatches,
              participantCountFromMatches,
            };
          } catch (error: any) {
            setLoadingParticipants(prev => {
              const newSet = new Set(prev);
              newSet.delete(tcId);
              return newSet;
            });

            logger.error(`Error fetching participants for category ${tcId}:`, {
              error,
              message: error?.message,
              response: error?.response?.data,
              status: error?.response?.status,
              tcId,
              categoryName: category.name,
              weightCategoryId: category.categoryId
            });
            const categoryMatches = matchesByCategoryId.get(category.id) || [];
            const participantIdsInMatches = new Set<number>();
            categoryMatches.forEach((m: any) => {
              if (m.participant1Id) participantIdsInMatches.add(Number(m.participant1Id));
              if (m.participant2Id) participantIdsInMatches.add(Number(m.participant2Id));
            });
            return {
              ...category,
              participants: [],
              hasDraw: false,
              matches: categoryMatches,
              participantCountFromMatches: participantIdsInMatches.size,
            };
          }
        })
      );
      
      const totalParticipants = categoriesWithParticipants.reduce((sum, cat) => sum + (cat.participants?.length || 0), 0);
      
      setCategories(sortTournamentCategories(categoriesWithParticipants));

      if (totalParticipants === 0 && categoriesWithParticipants.length > 0) {
        let totalRegistered = 0;
        try {
          const count = await TournamentService.getParticipantCount(tournamentId);
          totalRegistered = count;
        } catch {
          // игнорируем — для диагностики
        }
        // Если есть зарегистрированные, но ни в одной категории — пробуем автоназначить по users.categoryId
        if (totalRegistered > 0) {
          try {
            const { updated } = await TournamentService.syncParticipantCategories(tournamentId);
            if (updated > 0) {
              message.success(`Автоматически назначено категорий: ${updated} участников. Обновляю список.`);
              fetchCategories(); // повторная загрузка
              return;
            }
          } catch (syncErr) {
            logger.warn('syncParticipantCategories failed:', syncErr);
          }
        }
        logger.warn('No participants found in any category', {
          tournamentId,
          categoriesCount: categoriesWithParticipants.length,
          totalRegisteredInTournament: totalRegistered,
          sampleCategories: categoriesWithParticipants.slice(0, 3).map(cat => ({ id: cat.id, name: cat.name, categoryId: cat.categoryId }))
        });
        const msg = totalRegistered > 0
          ? 'Участники зарегистрированы на турнир, но не распределены по категориям. Проверьте раздел «Подтверждение регистрации» и назначьте участников в категории.'
          : 'В турнире нет зарегистрированных участников. Зарегистрируйте участников через раздел «Подтверждение регистрации» или «Регистрация участников».';
        message.warning({ content: msg, duration: 8 });
      }
    } catch (error) {
      logger.error('Error fetching categories:', error);
      message.error('Ошибка при загрузке категорий');
    } finally {
      setLoading(false);
    }
  };

  const handleCategorySelect = (categoryId: number, event: React.MouseEvent) => {
    if (event.ctrlKey || event.metaKey) {
      // Множественный выбор с Ctrl/Cmd
      const newSelected = new Set(selectedCategories);
      if (newSelected.has(categoryId)) {
        newSelected.delete(categoryId);
      } else {
        newSelected.add(categoryId);
      }
      setSelectedCategories(newSelected);
    } else {
      // Одиночный выбор
      setSelectedCategory(categoryId);
      setSelectedCategories(new Set([categoryId]));
    }
  };

  // @ts-expect-error - reserved for future participant reorder feature
  const _handleDragEnd = async (event: any, categoryId: number) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const category = categories.find(c => c.id === categoryId);
    if (!category) return;

    const oldIndex = category.participants.findIndex(p => p.id === active.id);
    const newIndex = category.participants.findIndex(p => p.id === over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    const newParticipants = [...category.participants];
    const [removed] = newParticipants.splice(oldIndex, 1);
    newParticipants.splice(newIndex, 0, removed);

    // Обновляем локально
    const updatedCategories = categories.map(cat => {
      if (cat.id === categoryId) {
        return {
          ...cat,
          participants: newParticipants,
        };
      }
      return cat;
    });
    setCategories(updatedCategories);

    // Отправляем на сервер для сохранения порядка
    try {
      await TournamentService.updateParticipantSeedingOrder(
        tournamentId,
        categoryId,
        newParticipants.map(p => p.id)
      );
      message.success('Порядок участников обновлен');
    } catch (error) {
      logger.error('Error updating participant order:', error);
      message.error('Ошибка при обновлении порядка участников');
      // Откатываем изменения
      setCategories(categories);
    }
  };

  // @ts-expect-error - reserved for per-category generate
  const handleGenerateDraw = async (categoryIds?: number[]) => {
    try {
      setGenerating(true);

      // Если выбраны конкретные категории, генерируем для них
      // Если categoryIds не указан, генерируем для всех категорий
      if (categoryIds && categoryIds.length > 0) {
        // Подсчитываем общее количество участников для выбранных категорий
        let totalParticipants = 0;
        categoryIds.forEach(catId => {
          const cat = categories.find(c => c.id === catId);
          if (cat) {
            totalParticipants += cat.participants?.length || 0;
          }
        });

        // Автоматически подбираем параметры
        const params = calculateOptimalParams(totalParticipants);

        // Генерируем жеребьевку для каждой выбранной категории
        for (const categoryId of categoryIds) {
          await TournamentService.generateBrackets(tournamentId, params.numTatami, categoryId);
        }

        message.success(`Жеребьевка успешно сгенерирована для ${categoryIds.length} категорий (${params.numTatami} татами, ${params.generateType === 'olympic' ? 'олимпийская система' : 'круговая система'})`);
      } else {
        // Подсчитываем общее количество участников во всех категориях
        const totalParticipants = categories.reduce((sum, cat) => sum + (cat.participants?.length || 0), 0);

        // Автоматически подбираем параметры
        const params = calculateOptimalParams(totalParticipants);

        // Генерируем для всех категорий (без указания categoryId)
        await TournamentService.generateBrackets(tournamentId, params.numTatami);
        message.success(`Пулы успешно сформированы для всех категорий (${params.numTatami} татами, ${params.generateType === 'olympic' ? 'олимпийская система' : 'круговая система'})`);
      }

      // Обновляем только локальные данные категорий/матчей
      await fetchCategories();
      setBracketKey(prev => prev + 1);
    } catch (error: any) {
      logger.error('Error generating draw:', error);
      message.error(error?.response?.data?.message || error?.message || 'Ошибка при формировании пулов');
    } finally {
      setGenerating(false);
    }
  };

  const handleGenerateAll = () => {
    // Сбрасываем количество татами на значение по умолчанию (1)
    setNumTatami(1);
    setIsGenerateModalVisible(true);
  };

  const handleConfirmGenerate = async () => {
    if (!numTatami || numTatami < 1) {
      message.warning('Укажите количество татами (минимум 1)');
      return;
    }

    try {
      setGenerating(true);
      setIsGenerateModalVisible(false);

      // Генерируем для всех категорий с указанным количеством татами
      await TournamentService.generateBrackets(tournamentId, numTatami);
      message.success(`Пулы успешно сформированы для всех категорий (${numTatami} татами)`);

      // Обновляем контент без перезагрузки страницы
      await fetchCategories();
      setBracketKey(prev => prev + 1);
      await onBracketsGenerated?.();
    } catch (error: any) {
      logger.error('Error generating draw:', error);
      message.error(error?.response?.data?.message || error?.message || 'Ошибка при формировании пулов');
    } finally {
      setGenerating(false);
    }
  };

  const handleRegeneratePdf = async () => {
    try {
      setRegeneratingPdf(true);
      message.loading({ content: 'Переформирование PDF...', key: 'regenerate-pdf', duration: 0 });
      await TournamentService.regenerateBracketsPdf(tournamentId);
      message.success({ content: 'PDF сеток успешно переформирован.', key: 'regenerate-pdf' });
    } catch (error: any) {
      logger.error('Error regenerating brackets PDF:', error);
      const serverMsg = error?.response?.data?.message ?? '';
      const isChromeMissing = /libnspr4|shared libraries|browser process|Failed to launch/i.test(serverMsg);
      const content = isChromeMissing
        ? 'Генерация PDF на сервере недоступна: не установлены зависимости для Chrome. Администратору нужно установить пакеты (см. docs/PDF_GENERATION_SERVER.md).'
        : (serverMsg || 'Ошибка при переформировании PDF');
      message.error({ content, key: 'regenerate-pdf', duration: 8 });
    } finally {
      setRegeneratingPdf(false);
    }
  };

  const hasCategoriesNeedingReform = categories.some((c: any) => c.bracketNeedsReform === true);

  const handleReformBrackets = async () => {
    try {
      setReforming(true);
      message.loading({ content: 'Переформирование сеток...', key: 'reform-brackets', duration: 0 });
      const { reformedCount } = await TournamentService.reformBrackets(tournamentId);
      message.success({
        content: reformedCount > 0
          ? `Переформировано сеток: ${reformedCount}.`
          : 'Нет категорий, требующих переформирования.',
        key: 'reform-brackets'
      });
      await fetchCategories();
      setBracketKey(prev => prev + 1);
      await onBracketsGenerated?.();
    } catch (error: any) {
      logger.error('Error reforming brackets:', error);
      message.error({
        content: error?.response?.data?.message || 'Ошибка при переформировании сеток',
        key: 'reform-brackets'
      });
    } finally {
      setReforming(false);
    }
  };

  // Проверка прав доступа для просмотра сетки
  const isAdmin = user?.role === 'admin';
  const isOrganizer = tournament && user && tournament.organizerId === user.id;
  const isManagerOrCoach = user && (user.role === 'manager' || user.role === 'head_manager' || user.role === 'coach');
  const isTournamentClubMember = tournament && user && tournament.clubId && user.clubId && tournament.clubId === user.clubId;
  const canViewBrackets = isAdmin || isOrganizer || (isManagerOrCoach && isTournamentClubMember) || bracketsPublished;

  const currentCategory = categories.find(c => c.id === selectedCategory);
  const activeParticipants = currentCategory?.participants.filter(p => p.active !== false) || [];

  // Преобразуем участников в формат для ClassicBracketView (id = user id для совпадения с match.participant1Id/2Id)
  const bracketParticipants: Participant[] = activeParticipants.map(p => ({
    id: (p as any).participantId ?? p.participant?.id ?? (p as any).userId ?? p.id,
    firstName: p.participant?.firstName ?? (p as any).firstName ?? '',
    lastName: p.participant?.lastName ?? (p as any).lastName ?? '',
    middleName: p.participant?.middleName ?? (p as any).middleName ?? '',
    dateOfBirth: '',
    gender: 'male' as 'male' | 'female',
    weight: p.participant?.weight ?? (p as any).weight ?? 0,
    beltLevel: p.participant?.beltLevel ?? (p as any).beltLevel ?? '',
    status: 'active' as 'active' | 'inactive',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    clubName: p.participant?.clubName ?? (p as any).clubName ?? '',
  }));

  return (
    <div>
      <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Title level={3} style={{ margin: 0 }}>Жеребьевка</Title>
        <Space>
          {hasMatches && hasCategoriesNeedingReform && (
            <Button
              type="primary"
              icon={<ReloadOutlined />}
              onClick={handleReformBrackets}
              loading={reforming}
              size="large"
            >
              Переформировать
            </Button>
          )}
          <Button
            type="primary"
            icon={<AppstoreOutlined />}
            onClick={handleGenerateAll}
            disabled={categories.length === 0 || generating || hasMatches}
            loading={generating}
            size="large"
            title={hasMatches ? 'Сетки уже сформированы' : undefined}
          >
            Сформировать сетки
          </Button>
          {hasMatches && bracketsPublished && canViewBrackets && (
            <Button
              icon={<FilePdfOutlined />}
              onClick={handleRegeneratePdf}
              loading={regeneratingPdf}
              size="large"
            >
              Переформировать PDF
            </Button>
          )}
          {hasMatches && !bracketsPublished && onPublishBrackets && (
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={onPublishBrackets}
              loading={publishingBrackets}
              size="large"
            >
              Сохранить и опубликовать сетки
            </Button>
          )}
        </Space>
      </div>

      {hasMatches && hasCategoriesNeedingReform && (() => {
        const categoriesNeedingReform = categories.filter((c: any) => c.bracketNeedsReform === true);
        return (
          <Card style={{ marginBottom: '24px', backgroundColor: '#e6f7ff', border: '1px solid #91d5ff' }}>
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
              <Text strong style={{ color: '#0050b3' }}>
                Следующие категории изменились (добавлены или сняты участники) и требуют переформирования сетки:
              </Text>
              <ul style={{ margin: 0, paddingLeft: '20px' }}>
                {categoriesNeedingReform.map((cat: any) => (
                  <li key={cat.id}>
                    <Text>{cat.name}</Text>
                  </li>
                ))}
              </ul>
              <Text type="secondary">
                Нажмите кнопку «Переформировать» выше, чтобы пересобрать сетки только по этим категориям.
              </Text>
            </Space>
          </Card>
        );
      })()}

      {categories.length > 0 && categories.every(cat => (cat.participants?.length || 0) === 0) && (
        <Card style={{ marginBottom: '24px', backgroundColor: '#fff7e6', border: '1px solid #ffd591' }}>
          <Space direction="vertical" style={{ width: '100%' }}>
            <Text strong style={{ color: '#d46b08' }}>⚠️ Участники не найдены в категориях</Text>
            <Text type="secondary">
              Все категории показывают 0 участников. Проверьте раздел «Подтверждение регистрации» и убедитесь,
              что участники зарегистрированы на турнир и назначены в нужные категории.
            </Text>
          </Space>
        </Card>
      )}

      {(() => {
        const categoriesWithOneParticipant = categories.filter(
          (cat: any) => (cat.participants?.length ?? 0) === 1
        );
        if (categoriesWithOneParticipant.length === 0) return null;
        return (
          <Card style={{ marginBottom: '24px', backgroundColor: '#fff2e8', border: '1px solid #ffbb96' }}>
            <Space direction="vertical" style={{ width: '100%' }} size="small">
              <Text strong style={{ color: '#d4380d' }}>
                Для следующих категорий невозможно создать сетку (1 участник):
              </Text>
              <ul style={{ margin: 0, paddingLeft: '20px' }}>
                {categoriesWithOneParticipant.map((cat: any) => (
                  <li key={cat.id}>
                    <Text>{cat.name}</Text>
                  </li>
                ))}
              </ul>
              <Text type="secondary">
                Для формирования сетки в категории нужно минимум 2 участника. Добавьте участников в эти категории или зарегистрируйте их на турнир.
              </Text>
            </Space>
          </Card>
        );
      })()}

      <Row gutter={16}>
        <Col span={8}>
          <Card title="Категории" style={{ height: 'calc(100vh - 200px)', overflowY: 'auto' }}>
            {loading ? (
              <Spin />
            ) : categories.length === 0 ? (
              <Empty description="Нет категорий" />
            ) : (
              <List
                dataSource={categories}
                renderItem={(category) => (
                  <List.Item
                    style={{
                      cursor: 'pointer',
                      backgroundColor: selectedCategory === category.id ? '#e6f7ff' : undefined,
                      border: selectedCategories.has(category.id) ? '2px solid #1890ff' : '1px solid #d9d9d9',
                      borderRadius: '4px',
                      marginBottom: '8px',
                      padding: '12px',
                    }}
                    onClick={(e) => handleCategorySelect(category.id, e)}
                  >
                    <Space direction="vertical" style={{ width: '100%' }}>
                      <Text strong>{category.name}</Text>
                      <Space>
                        {loadingParticipants.has(category.id) ? (
                          <Tag color="processing">
                            <Spin size="small" style={{ marginRight: 4 }} />
                            Загрузка...
                          </Tag>
                        ) : (
                          <Tag color="blue">
                            <UserOutlined />{' '}
                            {category.hasDraw
                              ? ((category as any).participantCountFromMatches ?? category.participants?.length ?? 0)
                              : (category.participants?.length ?? 0)}
                            {' участ.'}
                          </Tag>
                        )}
                        {(category.participants?.length ?? 0) === 1 && !category.hasDraw && (
                          <Tag color="orange">Сетку создать нельзя</Tag>
                        )}
                        {category.hasDraw && (
                          <Tag color="green">Жеребьевка создана</Tag>
                        )}
                      </Space>
                    </Space>
                  </List.Item>
                )}
              />
            )}
          </Card>
        </Col>
        <Col span={16}>
          {selectedCategory ? (
            <Card
              title={
                <Space>
                  <Text strong>{currentCategory?.name}</Text>
                  {currentCategory?.hasDraw && (
                    <Button
                      type="primary"
                      icon={<EditOutlined />}
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        setIsEditBracketModalVisible(true);
                      }}
                    >
                      Редактировать
                    </Button>
                  )}
                </Space>
              }
            >
              {currentCategory?.hasDraw && canViewBrackets ? (
                <ClassicBracketView
                  key={bracketKey}
                  tournamentId={tournamentId}
                  category={currentCategory}
                  participants={bracketParticipants}
                  tournamentDate={tournamentDate}
                  bracketsPublished={bracketsPublished}
                />
              ) : currentCategory?.hasDraw && !canViewBrackets ? (
                <Empty description="Сетка еще не опубликована. Дождитесь публикации сеток менеджером турнира." />
              ) : activeParticipants.length < 2 ? (
                <Empty description="Недостаточно активных участников для формирования сетки (минимум 2). Нажмите «Сформировать сетки» выше для генерации всех сеток или выберите категорию с участниками." />
              ) : (
                <Empty
                  description={
                    <Space direction="vertical" align="center">
                      <Text>Используйте кнопку «Сформировать сетки» выше для генерации сеток по всем категориям</Text>
                      <Text type="secondary">Сетка появится здесь после генерации</Text>
                    </Space>
                  }
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              )}
            </Card>
          ) : (
            <Card>
              <Empty description="Выберите категорию для просмотра участников" />
            </Card>
          )}
        </Col>
      </Row>

      <Modal
        title="Сформировать сетки"
        open={isGenerateModalVisible}
        onOk={handleConfirmGenerate}
        onCancel={() => setIsGenerateModalVisible(false)}
        confirmLoading={generating}
        okText="Сформировать"
        cancelText="Отмена"
      >
        <p>Вы уверены, что хотите сформировать сетки для всех категорий этого турнира?</p>
        <p>Это действие распределит всех зарегистрированных участников по сеткам в соответствии с их категориями.</p>
        <p>При формировании сеток будут учтены следующие условия:</p>
        <ul>
          <li>Максимально развести бойцов от одного тренера в разные ветки</li>
          <li>Максимально развести бойцов с одного клуба</li>
          <li>Максимально развести бойцов с одного города</li>
          <li>Максимально развести бойцов с одной федерации</li>
        </ul>
        <div style={{ marginTop: '16px' }}>
          <Typography.Text strong>Количество татами:</Typography.Text>
          <InputNumber
            min={1}
            max={10}
            value={numTatami}
            onChange={(value) => setNumTatami(value || 1)}
            style={{ marginLeft: '8px', width: '100px' }}
          />
        </div>
      </Modal>

      <Modal
        title={`Редактирование сетки: ${currentCategory?.name || ''}`}
        open={isEditBracketModalVisible}
        getContainer={document.body}
        maskClosable={false}
        onCancel={() => {
          setIsEditBracketModalVisible(false);
          fetchCategories();
          setBracketKey(prev => prev + 1);
        }}
        footer={
          <Space>
            <Button onClick={() => setIsEditBracketModalVisible(false)}>Отмена</Button>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              onClick={async () => {
                await fetchCategories();
                setBracketKey(prev => prev + 1);
                setIsEditBracketModalVisible(false);
                message.success('Изменения сохранены');
              }}
            >
              Сохранить
            </Button>
          </Space>
        }
        width="95%"
        style={{ top: 20, maxWidth: 'calc(100vw - 48px)', paddingBottom: 0 }}
        styles={{ body: { maxHeight: 'calc(100vh - 160px)', minHeight: 400, overflow: 'auto' } }}
        zIndex={2000}
        destroyOnClose
      >
        {currentCategory && (
          <ClassicBracketView
            key={`edit-${bracketKey}`}
            tournamentId={tournamentId}
            category={currentCategory}
            participants={bracketParticipants}
            tournamentDate={tournamentDate}
            bracketsPublished={bracketsPublished}
          />
        )}
      </Modal>
    </div>
  );
};
